'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@myapp/ui/components/button';

export default function RefundPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              홈으로 돌아가기
            </Link>
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">환불정책</h1>
          
          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">1. 환불 정책 개요</h2>
              <p className="text-gray-600 mb-4">
                강원푸드핏은 고객님의 만족을 최우선으로 생각하며, 합리적이고 투명한 환불 정책을 운영하고 있습니다. 
                본 정책은 전자상거래 등에서의 소비자보호에 관한 법률에 따라 작성되었습니다.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">2. 신선 농산물 환불 규정</h2>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-green-800 mb-2">즉시 환불 가능한 경우</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>배송 중 상품이 훼손되거나 변질된 경우</li>
                  <li>주문한 상품과 다른 상품이 배송된 경우</li>
                  <li>상품에 이물질이 발견된 경우</li>
                  <li>유통기한이 경과한 상품이 배송된 경우</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-yellow-800 mb-2">부분 환불 가능한 경우</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>배송된 수량 중 일부만 품질 문제가 있는 경우</li>
                  <li>포장 손상으로 인한 경미한 품질 저하가 있는 경우</li>
                  <li>예상 배송일보다 2일 이상 지연된 경우 (10% 할인)</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-medium text-red-800 mb-2">환불 불가능한 경우</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>고객의 단순 변심 (신선식품 특성상)</li>
                  <li>고객의 부주의로 인한 상품 훼손</li>
                  <li>수령 후 7일이 경과한 경우</li>
                  <li>재판매가 불가능할 정도로 상품 가치가 감소한 경우</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">3. 환불 신청 절차</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <ol className="list-decimal pl-6 text-gray-600 space-y-3">
                  <li>
                    <strong>환불 신청:</strong> 마이페이지 또는 고객센터(033-248-1000)로 신청
                  </li>
                  <li>
                    <strong>사진 증빙:</strong> 문제가 된 상품의 사진을 촬영하여 제출
                  </li>
                  <li>
                    <strong>검토 및 승인:</strong> 접수 후 24시간 이내 검토 및 결과 안내
                  </li>
                  <li>
                    <strong>환불 처리:</strong> 승인 후 3~5 영업일 이내 원 결제수단으로 환불
                  </li>
                </ol>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">4. 배송비 환불 규정</h2>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">구분</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">배송비 부담</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">상품 하자로 인한 환불</td>
                    <td className="border border-gray-300 px-4 py-2">강원푸드핏 전액 부담</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">오배송으로 인한 교환</td>
                    <td className="border border-gray-300 px-4 py-2">강원푸드핏 전액 부담</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">고객 변심 (가능한 경우)</td>
                    <td className="border border-gray-300 px-4 py-2">왕복 배송비 고객 부담</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">5. 특별 보상 정책</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 mb-3">
                  <strong>품질 보증 프로그램</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>최초 구매 고객: 100% 만족 보장 (전액 환불 또는 재배송)</li>
                  <li>프리미엄 회원: 품질 문제 시 120% 적립금 보상</li>
                  <li>정기 구독 고객: 다음 주문 시 30% 추가 할인</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">6. 분쟁 해결 절차</h2>
              <p className="text-gray-600 mb-4">
                환불 관련 분쟁이 발생한 경우, 다음의 절차를 통해 해결할 수 있습니다:
              </p>
              <ol className="list-decimal pl-6 text-gray-600 space-y-2">
                <li>고객센터를 통한 1차 협의 (033-248-1000)</li>
                <li>소비자보호팀 에스컬레이션 (refund@gangwonfoodfit.kr)</li>
                <li>한국소비자원 분쟁조정 신청</li>
                <li>전자거래분쟁조정위원회 조정 신청</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">7. 자주 묻는 질문</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="font-medium text-gray-700 mb-1">Q: 신선식품도 환불이 가능한가요?</p>
                  <p className="text-gray-600">A: 네, 품질 문제가 있는 경우 증빙자료와 함께 신청하시면 환불 가능합니다.</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="font-medium text-gray-700 mb-1">Q: 환불은 얼마나 걸리나요?</p>
                  <p className="text-gray-600">A: 승인 후 카드는 3~5일, 계좌이체는 1~2일 내 처리됩니다.</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="font-medium text-gray-700 mb-1">Q: 일부만 문제가 있어도 전체 환불이 가능한가요?</p>
                  <p className="text-gray-600">A: 문제가 있는 상품에 대해서만 부분 환불이 원칙이나, 상황에 따라 전체 환불도 가능합니다.</p>
                </div>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="bg-gray-100 rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">고객센터 안내</h3>
                <p className="text-gray-600">
                  전화: 033-248-1000 (평일 09:00 ~ 18:00)<br />
                  이메일: refund@gangwonfoodfit.kr<br />
                  카카오톡: @강원푸드핏
                </p>
              </div>
              <p className="text-sm text-gray-500 text-center mt-6">
                최종 수정일: 2025년 1월 1일<br />
                버전: 1.0
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}